import { Button, SelectMenu, Table, TextField } from '@scale/scaleui';
import {
  SquadMemberRole,
  SquadType,
  TeamLeadPermissionRole,
} from '@scale/shared/types/expertManagement';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CreateSquadModal } from 'react-scale/components/ExpertManagement/CommunityManagement/CreateSquadModal';
import { EditSquadDrawer } from 'react-scale/components/ExpertManagement/CommunityManagement/EditSquadDrawer';
import { LoadingTable } from 'react-scale/components/ExpertManagement/ExpertManagementPage/LoadingTable';
import { useFetchSquads } from 'react-scale/components/ExpertManagement/hooks/queries/useFetchSquads';
import { useFilteredPodTypeOptions } from 'react-scale/components/ExpertManagement/hooks/useFilteredPodTypeOptions';
import { useIsCurrentUserAuthorized } from 'react-scale/components/ExpertManagement/hooks/useIsCurrentUserAuthorized';
import { Routes, buildRoute } from 'react-scale/components/ExpertManagement/urls';
import { Flex, Link, Icon, Text } from '@scale/scaleui-radix';
import { faExternalLinkAlt } from '@fortawesome/pro-regular-svg-icons';

export const SQUAD_TYPE_TO_NAME_MAP: Record<SquadType, string> = {
  [SquadType.TeamLeadCoordinator]: 'Team Lead Coordinator',
  [SquadType.Squad]: 'Squad',
  [SquadType.Community]: 'Community',
  [SquadType.CommunitySubSquad]: 'Sub Squad',
};

const SQUAD_TYPE_SORT_MAP: Record<SquadType, number> = {
  [SquadType.TeamLeadCoordinator]: 0,
  [SquadType.Squad]: 1,
  [SquadType.Community]: 2,
  [SquadType.CommunitySubSquad]: 3,
};

export const CommunityChannelLink: React.FC<{ communityChannel: string }> = ({
  communityChannel,
}) => (
  <Link
    href={`https://dashboard.scale.com/corp/genai-ops-hub/community-management/channel/${communityChannel}`}
    target="_blank"
    rel="noopener noreferrer"
    size="1"
  >
    <Flex gap="1" align="center">
      <Text weight="medium" size="1">
        {communityChannel}
      </Text>
      <Icon icon={faExternalLinkAlt} label="link" size="1" />
    </Flex>
  </Link>
);

export const SquadsManagement: React.FC = () => {
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const [createPanelOpen, setCreatePanelOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedSquad, setSelectedSquad] = useState<any>(undefined);
  const [editMode, setEditMode] = useState<'edit' | 'create'>('edit');

  const isGlobalUser = useIsCurrentUserAuthorized([
    TeamLeadPermissionRole.SuperAdmin,
    TeamLeadPermissionRole.GlobalCommunityManager,
  ]);
  const filteredPodTypes = useFilteredPodTypeOptions().sort((a, b) => a.name.localeCompare(b.name));
  const podTypeOptions = useMemo(
    () => [...(isGlobalUser ? [{ id: 'all', name: 'All' }] : []), ...filteredPodTypes],
    [isGlobalUser, filteredPodTypes],
  );

  const keyedPods = useMemo(
    () => Object.fromEntries(filteredPodTypes.map(p => [p.id, p.name])),
    [filteredPodTypes],
  );

  const [podTypeFilter, setPodTypeFilter] = useState(podTypeOptions[0]?.id);

  useEffect(() => {
    setPodTypeFilter(podTypeOptions[0]?.id);
  }, [podTypeOptions]);

  const {
    data: squads,
    isLoading,
    refetch,
  } = useFetchSquads(podTypeFilter !== 'all' ? podTypeFilter : undefined);

  const baseUrl = '/' + location.pathname.split('/').slice(1, 3).join('/');

  const sortedData = useMemo(() => {
    if (!squads) {
      return [];
    }
    // Sort by squad type, then team lead pod type, then by team lead coordinator name, then by team lead name
    return squads
      .sort((a, b) => {
        const aName = a.leaders?.find(l => l.role === SquadMemberRole.TeamLead)?.name ?? '';
        const bName = b.leaders?.find(l => l.role === SquadMemberRole.TeamLead)?.name ?? '';
        return aName.localeCompare(bName);
      })
      .sort((a, b) => {
        const aName =
          a.leaders?.find(l => l.role === SquadMemberRole.TeamLeadCoordinator)?.name ?? '';
        const bName =
          b.leaders?.find(l => l.role === SquadMemberRole.TeamLeadCoordinator)?.name ?? '';
        return aName.localeCompare(bName);
      })
      .sort((a: any, b: any) =>
        keyedPods[a.teamLeadPodType]?.localeCompare(keyedPods[b.teamLeadPodType]),
      )
      .sort(
        (a: any, b: any) =>
          SQUAD_TYPE_SORT_MAP[a.type as SquadType] - SQUAD_TYPE_SORT_MAP[b.type as SquadType],
      );
  }, [keyedPods, squads]);

  const refetchSquads = useCallback(async () => {
    const resp = await refetch();
    if (selectedSquad) {
      const newSelectedSquad = resp.data?.find((s: any) => s._id === selectedSquad._id);
      if (newSelectedSquad) {
        setSelectedSquad(newSelectedSquad);
      } else {
        setSelectedSquad(undefined);
        setDetailPanelOpen(false);
      }
    }
  }, [refetch, selectedSquad]);

  const podTypeColumns = [
    {
      id: 'name',
      header: 'Community Name',
      cell: function UserCell(props: any) {
        const { name, count, type, leaders } = props.row.original;
        if (!name) {
          return null;
        }

        const userToLink =
          type === 'team_lead_coordinator'
            ? leaders.find((l: any) => l.role === 'team_lead_coordinator')
            : leaders.find((l: any) => l.role === 'team_lead') ??
              leaders.find((l: any) => l.role === 'team_lead_coordinator');

        return (
          <a
            href={baseUrl + buildRoute(Routes.ExpertManagement, { user: userToLink?.id })}
            className="flex flex-col"
          >
            {name} ({count ?? 0})
          </a>
        );
      },
    },
    {
      id: 'communityChannel',
      header: 'Channel ID',
      cell: function ChannelCell(props: any) {
        const { communityChannel } = props.row.original;
        if (!communityChannel) return null;
        return <CommunityChannelLink communityChannel={communityChannel} />;
      },
    },
    {
      id: 'teamLeadPodType',
      header: 'Pod Type',
      cell: function PodTypeCell(props: any) {
        const { teamLeadPodType } = props.row.original;
        return <div>{keyedPods[teamLeadPodType] ?? '-'}</div>;
      },
    },
    {
      id: 'type',
      header: 'Type',
      cell: function TypeCell(props: any) {
        const { type } = props.row.original;
        return <div>{type ? SQUAD_TYPE_TO_NAME_MAP[type as SquadType] : '-'}</div>;
      },
    },
    {
      id: 'teamLeads',
      header: 'Team Lead(s)',
      cell: function UserCell(props: any) {
        const { leaders, type } = props.row.original;
        const leader =
          type !== SquadType.TeamLeadCoordinator
            ? leaders.find((l: any) => l.role === 'team_lead')
            : undefined;
        return (
          <div className="flex flex-col">
            {leader ? (
              <>
                <div>{leader.name}</div>
                <div style={{ fontSize: '10px' }}>{leader.email}</div>
              </>
            ) : (
              '-'
            )}
          </div>
        );
      },
    },
    {
      id: 'teamLeadCoordinators',
      header: 'Team Lead Coordinator',
      cell: function UserCell(props: any) {
        const { leaders } = props.row.original;
        const leader = leaders.find((l: any) => l.role === 'team_lead_coordinator');
        return (
          <div className="flex flex-col">
            {leader ? (
              <>
                <div>{leader.name}</div>
                <div style={{ fontSize: '10px' }}>{leader.email}</div>
              </>
            ) : (
              '-'
            )}
          </div>
        );
      },
    },
    {
      id: 'editButton',
      header: '',
      enableSorting: false,
      cell: function EditCell(current: any) {
        return (
          <Button
            onClick={() => {
              setEditMode('edit');
              setSelectedSquad(current.row.original);
              setDetailPanelOpen(true);
            }}
          >
            Edit
          </Button>
        );
      },
    },
  ];

  const closeModal = useCallback(() => {
    setDetailPanelOpen(false);
  }, [setDetailPanelOpen]);

  const filteredData = useMemo(() => {
    if (!searchValue) {
      return sortedData;
    }
    return sortedData.filter(
      (d: any) =>
        d.name.toLowerCase().includes(searchValue.toLowerCase()) ||
        d.leaders.some((l: any) =>
          l.name
            .toLowerCase()
            .includes(
              searchValue.toLowerCase() ||
                l.email.toLowerCase().includes(searchValue.toLowerCase()),
            ),
        ),
    );
  }, [sortedData, searchValue]);

  return (
    <>
      <div style={{ minWidth: '70%' }} className="flex flex-col gap-4">
        <div className="flex justify-between">
          <TextField
            className="w-[300px]"
            placeholder="Search by squad name or TL/TLC name or email.."
            value={searchValue}
            onChange={t => setSearchValue(t.target.value)}
            trailingIcon={'xmark'}
            onTrailingIconClick={() => setSearchValue('')}
          />
          <div className="flex gap-2">
            <div className="flex gap-2 my-auto">
              <Button onClick={() => setCreatePanelOpen(true)}>Create TLC Squad</Button>
            </div>
            <SelectMenu
              selectedOptionId={podTypeFilter}
              setSelected={r => setPodTypeFilter(r)}
              options={podTypeOptions}
            />
          </div>
        </div>

        {isLoading || !squads ? (
          <LoadingTable columns={podTypeColumns} numRows={3} rowHeight={2} />
        ) : (
          <Table
            columns={podTypeColumns}
            data={filteredData}
            defaultPageSize={50}
            divideColumns={false}
          />
        )}
      </div>
      <EditSquadDrawer
        editMode={editMode}
        loading={isLoading}
        onClose={closeModal}
        open={detailPanelOpen}
        refetchSquads={refetchSquads}
        squad={selectedSquad}
      />
      <CreateSquadModal
        onClose={() => setCreatePanelOpen(false)}
        open={createPanelOpen}
        refetch={refetchSquads}
        defaultTeamLeadPodType={podTypeFilter}
      />
    </>
  );
};
